import React, { useMemo } from 'react';
import { Card } from 'antd';

const LEVEL_WIDTH = 140;
const NODE_HEIGHT = 50;
const START_X = 40;
const START_Y = 40;

export const ProbabilityTree = ({ config = {}, variables = {} }) => {
    const { layoutRoot, svgWidth, svgHeight } = useMemo(() => {
        const rawRoot = {
            id: 'root',
            label: config.startLabel || "Start",
            prob: "",
            children: config.branches || []
        };

        let maxDepth = 0;
        let leafCounter = 0;

        // Path-based key generation (e.g., "root-0-1")
        const calculateNodePosition = (node, depth, pathId) => {
            if (depth > maxDepth) maxDepth = depth;

            // Generate stable ID
            const nodeId = pathId;

            if (!node.children || node.children.length === 0) {
                const currentY = leafCounter * NODE_HEIGHT;
                leafCounter++;
                return { ...node, y: currentY, children: [], id: nodeId };
            }

            const processedChildren = node.children.map((child, index) =>
                calculateNodePosition(child, depth + 1, `${nodeId}-${index}`)
            );

            const firstChildY = processedChildren[0].y;
            const lastChildY = processedChildren[processedChildren.length - 1].y;
            const centerY = (firstChildY + lastChildY) / 2;

            return { ...node, y: centerY, children: processedChildren, id: nodeId };
        };

        const processedRoot = calculateNodePosition(rawRoot, 0, 'root');
        const totalWidth = (maxDepth * LEVEL_WIDTH) + START_X + 150;
        const totalHeight = (leafCounter * NODE_HEIGHT) + START_Y + 40;

        return {
            layoutRoot: processedRoot,
            svgWidth: Math.max(totalWidth, 400),
            svgHeight: Math.max(totalHeight, 300)
        };
    }, [config]);

    const renderTree = (node, depth, parentX, parentY) => {
        const currentX = START_X + (depth * LEVEL_WIDTH);
        const currentY = START_Y + node.y;

        // Calc label width: approx 8px per char + 12px padding
        const labelWidth = node.prob ? (node.prob.length * 8 + 12) : 0;

        return (
            <g key={node.id}>
                {depth > 0 && (
                    <g>
                        <line x1={parentX} y1={parentY} x2={currentX} y2={currentY} stroke="#94a3b8" strokeWidth="2" />
                        {node.prob && (
                            <g>
                                <rect
                                    x={(parentX + currentX) / 2 - labelWidth/2}
                                    y={(parentY + currentY) / 2 - 9}
                                    width={labelWidth} height="18"
                                    fill="white" rx="4"
                                />
                                <text
                                    x={(parentX + currentX) / 2}
                                    y={(parentY + currentY) / 2 + 4}
                                    textAnchor="middle" fontSize="11" fill="#ef4444" fontWeight="bold"
                                >
                                    {node.prob}
                                </text>
                            </g>
                        )}
                    </g>
                )}

                <circle cx={currentX} cy={currentY} r={4} fill={depth === 0 ? "#333" : "#3b82f6"} />
                <text x={currentX + 10} y={currentY + 4} fontSize="13" fontWeight={depth === 0 ? "bold" : "normal"} fill="#1e293b">
                    {node.label}
                </text>

                {node.children.map((child) => renderTree(child, depth + 1, currentX, currentY))}
            </g>
        );
    };

    return (
        <div className="flexColumn gap-medium width-100">
            <div className="ch-emph-block" style={{ overflowX: 'auto', background: '#ffffff', border: '1px solid #e2e8f0' }}>
                <svg width={svgWidth} height={svgHeight} style={{ minWidth: '100%' }}>
                    {renderTree(layoutRoot, 0, 0, 0)}
                </svg>
            </div>
            <Card className="ch-card padding--m bg-light-gray border-all--s">
                <div className="flexSpaceBetween flexAlignCentre ch-text-small text-muted">
                    <span><strong>Rule:</strong> Multiply along branches for combined probability.</span>
                    <span>Sum probabilities of mutually exclusive outcomes.</span>
                </div>
            </Card>
        </div>
    );
};